import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { EnergiaPOD } from '../../services/energiaService';
import { energiaService } from '../../services/energiaService';
import meterReadingService from '../../services/meterReadingService';
import toast from 'react-hot-toast';

interface AssignPodModalProps {
  consumptionLocationId: string;
  customerId: string;
  onClose: () => void;
  onAssigned: () => void;
}

const AssignPodModal: React.FC<AssignPodModalProps> = ({
  consumptionLocationId,
  customerId,
  onClose,
  onAssigned
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [availablePods, setAvailablePods] = useState<EnergiaPOD[]>([]);
  const [filteredPods, setFilteredPods] = useState<EnergiaPOD[]>([]);
  const [selectedPod, setSelectedPod] = useState<EnergiaPOD | null>(null);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Új POD manuális hozzáadása
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualPodData, setManualPodData] = useState({
    podId: '',
    serialNumber: '',
    energyType: 'electricity' as 'electricity' | 'gas',
    provider: '',
    multiplier: 1.0
  });

  useEffect(() => {
    loadAvailablePods();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = availablePods.filter(pod =>
        pod.pod_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pod.provider.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPods(filtered);
    } else {
      setFilteredPods(availablePods);
    }
  }, [searchTerm, availablePods]);

  const loadAvailablePods = async () => {
    try {
      setLoading(true);
      // Betöltjük az összes POD-ot
      const allPods = await energiaService.getHybridPods();
      
      // Szűrjük azokat, amelyek még nincsenek hozzárendelve ehhez az ügyfélhez
      const unassignedPods = allPods.filter(pod => 
        !pod.customer_info?.customer_id || 
        pod.customer_info.customer_id !== customerId
      );
      
      setAvailablePods(unassignedPods);
      setFilteredPods(unassignedPods);
    } catch (err) {
      console.error('Error loading PODs:', err);
      setError('Hiba a POD-ok betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedPod) {
      toast.error('Válassz ki egy POD-ot!');
      return;
    }

    setAssigning(true);
    setError(null);

    try {
      console.log('POD hozzárendelése:', {
        podId: selectedPod.pod_id,
        customerId,
        consumptionLocationId
      });

      const success = await meterReadingService.linkMeterPointToCustomer(
        selectedPod.pod_id,
        customerId,
        consumptionLocationId
      );

      if (success) {
        toast.success(`POD ${selectedPod.pod_id} sikeresen hozzárendelve!`);
        onAssigned();
        onClose();
      } else {
        throw new Error('A hozzárendelés sikertelen volt');
      }
    } catch (err: any) {
      console.error('POD hozzárendelési hiba:', err);
      const errorMsg = err.message || 'Hiba történt a hozzárendelés során';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setAssigning(false);
    }
  };

  const handleManualAssign = async () => {
    if (!manualPodData.podId || manualPodData.podId.length !== 33) {
      const errorMsg = 'A POD-nak pontosan 33 karakter hosszúnak kell lennie';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setAssigning(true);
    setError(null);

    try {
      console.log('Új POD hozzárendelése:', {
        podId: manualPodData.podId,
        customerId,
        consumptionLocationId,
        serialNumber: manualPodData.serialNumber,
        provider: manualPodData.provider
      });

      // Létrehozzuk a mérőhelyet
      const success = await meterReadingService.linkMeterPointToCustomer(
        manualPodData.podId,
        customerId,
        consumptionLocationId
      );
      
      if (success) {
        toast.success(`POD ${manualPodData.podId} sikeresen létrehozva és hozzárendelve!`);
        onAssigned();
        onClose();
      } else {
        throw new Error('A hozzárendelés sikertelen volt');
      }
    } catch (err: any) {
      console.error('Manuális POD hozzárendelési hiba:', err);
      const errorMsg = err.message || 'Hiba történt a hozzárendelés során';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              POD hozzárendelése
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="px-6 py-4">
            {error && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}

            {/* Váltás meglévő és új POD között */}
            <div className="mb-4 flex space-x-2">
              <button
                onClick={() => setShowManualInput(false)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  !showManualInput
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                Meglévő POD kiválasztása
              </button>
              <button
                onClick={() => setShowManualInput(true)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  showManualInput
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                Új POD hozzáadása
              </button>
            </div>

            {!showManualInput ? (
              <>
                {/* Keresés */}
                <div className="mb-4">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="POD vagy provider keresése..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* POD lista */}
                <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
                  {loading ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      Betöltés...
                    </div>
                  ) : filteredPods.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      Nincs elérhető POD
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredPods.map((pod) => (
                        <div
                          key={pod.id}
                          onClick={() => setSelectedPod(pod)}
                          className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            selectedPod?.id === pod.id
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                              : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {pod.pod_id}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Provider: {pod.provider}
                              </p>
                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                                <span>Fogyasztás: {pod.total_consumption_kwh.toFixed(2)} kWh</span>
                                <span>Mérések: {pod.measurement_count}</span>
                              </div>
                            </div>
                            {selectedPod?.id === pod.id && (
                              <div className="flex-shrink-0">
                                <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center">
                                  <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                                    <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Manuális POD bevitel */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      POD azonosító * (33 karakter)
                    </label>
                    <input
                      type="text"
                      maxLength={33}
                      value={manualPodData.podId}
                      onChange={(e) => setManualPodData({ ...manualPodData, podId: e.target.value.toUpperCase() })}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono"
                      placeholder="HU000000000000000000000000000000"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {manualPodData.podId.length}/33 karakter
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Gyártási szám
                    </label>
                    <input
                      type="text"
                      value={manualPodData.serialNumber}
                      onChange={(e) => setManualPodData({ ...manualPodData, serialNumber: e.target.value })}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Energia típus *
                      </label>
                      <select
                        value={manualPodData.energyType}
                        onChange={(e) => setManualPodData({ ...manualPodData, energyType: e.target.value as any })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="electricity">Villany</option>
                        <option value="gas">Gáz</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Szorzó
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={manualPodData.multiplier}
                        onChange={(e) => setManualPodData({ ...manualPodData, multiplier: parseFloat(e.target.value) })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Provider
                    </label>
                    <input
                      type="text"
                      value={manualPodData.provider}
                      onChange={(e) => setManualPodData({ ...manualPodData, provider: e.target.value })}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="pl. OPUS, EON, MVMI, DEMASZ"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Mégse
            </button>
            <button
              onClick={showManualInput ? handleManualAssign : handleAssign}
              disabled={assigning || (!showManualInput && !selectedPod) || (showManualInput && manualPodData.podId.length !== 33)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {assigning ? 'Hozzárendelés...' : 'Hozzárendelés'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignPodModal;
